import React, { useState } from 'react';
import Button from '../Button';
import { MdOutlineDelete } from 'react-icons/md';

interface ImageFormPromoProps {
  image: string;
  handleRemove: () => void;
}
const ImageFormPromo = ({ image, handleRemove }: ImageFormPromoProps) => {
  const openImageInNewTab = () => {
    if (image) {
      const openedWindow = window.open(image, '_blank');
      if (openedWindow) {
        openedWindow.focus();
      }
    }
  };

  return (
    <div className="relative h-auto w-full">
      <div onClick={openImageInNewTab} className="cursor-pointer">
        <img src={image} alt="image" className="object-cover w-full max-h-[550px]" />
      </div>
      <div>
        <Button color="gray" className="absolute bottom-5 right-5" onClick={handleRemove}>
          <span className="flex flex-row gap-1 font-bold uppercase text-sm">
            <MdOutlineDelete size={'20px'} /> Hapus
          </span>
        </Button>
      </div>
    </div>
  );
};

interface DropzoneProps {
  onChange?: (file: File) => void;
  onRemove?: () => void;
  containerClassName?: string;
  initialValue?: string;
}

export default function Dropzone({
  onChange = () => {},
  onRemove = () => {},
  containerClassName,
  initialValue,
}: DropzoneProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<{
    dimensionError: string;
    sizeError: string;
  }>({
    dimensionError: '',
    sizeError: '',
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      // Check file size (2 MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        setError({
          dimensionError: '',
          sizeError: 'Batas maksimal upload 2MB',
        });
        return;
      } else {
        setError({
          dimensionError: '',
          sizeError: '',
        });
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new window.Image();
        img.src = e.target.result as string;
        img.onload = () => {
          const aspectRatio = 16 / 9;

          const width = img.width;
          const height = img.height;
          const currentAspectRatio = width / height;

          // Calculate new dimensions
          let newWidth, newHeight;

          if (currentAspectRatio > aspectRatio) {
            // If the image is wider than the desired aspect ratio
            newWidth = height * aspectRatio;
            newHeight = height;
          } else {
            // If the image is taller than the desired aspect ratio
            newWidth = width;
            newHeight = width / aspectRatio;
          }

          // Create a canvas to draw the resized image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Calculate the position to center the image
          const offsetX = (newWidth - width) / 2;
          const offsetY = (newHeight - height) / 2;

          // Draw the image on the canvas
          ctx.drawImage(img, offsetX, offsetY, width, height);

          setSelectedFile(file);

          if (onChange) {
            onChange(file);
          }
        };
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);

    if (onRemove) {
      onRemove();
    }
  };

  return (
    <div className={containerClassName}>
      <div className="block items-center justify-center md:w-[328px] mb-3">
        {!selectedFile && !initialValue && (
          <label
            htmlFor="dropzone-file"
            className="relative flex items-center justify-start w-full cursor-pointer"
          >
            <div className="relative flex flex-col items-center justify-center pt-5 pb-6">
              <Button
                className="!h-12 text-reliableBlack border-[1px] px-4 py-3 border-reliableBlack"
                variant="bg-white"
              >
                <p className="flex items-center gap-1 text-xs text-black font-bold uppercase">
                  Unggah
                </p>
              </Button>
            </div>
            <input
              id="dropzone-file"
              type="file"
              className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleFileChange}
            />
          </label>
        )}

        {selectedFile && (
          <ImageFormPromo image={URL.createObjectURL(selectedFile)} handleRemove={handleRemove} />
        )}

        {initialValue && selectedFile === null && (
          <ImageFormPromo image={initialValue} handleRemove={handleRemove} />
        )}

        {error.dimensionError && (
          <div className="flex items-center gap-[5.67px]">
            <span className="text-supportiveRed text-[14px] leading-[20px] font-[400]">
              {error.dimensionError}
            </span>
          </div>
        )}

        {error.sizeError && (
          <div className="flex items-center gap-[5.67px]">
            <span className="text-supportiveRed text-[14px] leading-[20px] font-[400]">
              {error.sizeError}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
